import * as R from 'ramda';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
// components
import { TextComponent } from '../text';
import ZIndexClick from '../z-index-click';
import { PopperComponent } from '../popper';
import { FormFooter } from '../form-footer';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// icons
import * as I from '../../svgs';
// ui
import { Box, Flex, Span } from '../../ui';
// component edit-report
import { FieldSelect } from '../edit-report/components/field-select';
import { setOptions, getAvailableForReportSort } from '../edit-report/helpers';
import { defaultSortFields } from '../edit-report/components/sort-list/settings';
// component report-filters
import { getFilterPopperSettings } from './helpers';
//////////////////////////////////////////////////

const SortEditor = ({
  sortField,
  handleSave,
  closePopper,
  availableFields,
  availableSortFields,
}) => {
  const [field, setField] = useState(sortField);

  useEffect(() => setField(sortField), [sortField]);

  const selectedOption = useMemo(
    () => R.find(R.propEq(R.prop(GC.FIELD_NAME, field), GC.FIELD_VALUE), availableFields),
    [field, availableFields],
  );

  const options = useMemo(
    () => setOptions(availableSortFields),
    [availableSortFields],
  );

  const handleSelectField = useCallback(({ data }) => {
    const {
      type,
      value,
      referenceFieldName,
    } = data;

    let nextField = {
      ...R.omit([GC.FIELD_REFERENCE_FIELD_NAME], field),
      name: value,
      reference: R.equals(type, 'reference'),
    };

    if (G.isNotNil(referenceFieldName)) {
      nextField = R.assoc(GC.FIELD_REFERENCE_FIELD_NAME, referenceFieldName, nextField);
    }

    setField(nextField);
  }, [field]);

  const handleToggleDirection = useCallback(() => {
    const direction = R.equals(R.prop('order', field), GC.SORT_TYPE_ASC)
      ? GC.SORT_TYPE_DESC
      : GC.SORT_TYPE_ASC;

    setField(R.assoc('order', direction, field));
  }, [field]);

  const direction = R.propOr(GC.SORT_TYPE_ASC, 'order', field);
  const directionIcon = R.equals(direction, GC.SORT_TYPE_ASC) ? I.arrowUp : I.arrowDown;

  return (
    <Box minWidth={330}>
      <Flex p={10} alignItems='center'>
        <FieldSelect
          fieldIndex={0}
          options={options}
          action={handleSelectField}
          useMenuPortalTarget={false}
          id='report-filters-sort-field'
          selectedOption={selectedOption}
          value={R.prop(GC.FIELD_NAME, field)}
        />
        <Span
          ml={5}
          cursor='pointer'
          title={direction}
          onClick={handleToggleDirection}
        >
          {directionIcon(G.getTheme('colors.dark.blue'))}
        </Span>
      </Flex>
      <FormFooter
        boxStyles={{ p: 10 }}
        cancelAction={closePopper}
        submitBtnStyles={{ height: 27 }}
        cancelBtnStyles={{ height: 27 }}
        submitDisabled={G.isNilOrEmpty(R.prop(GC.FIELD_NAME, field))}
        submitAction={() => {
          handleSave(field);
          closePopper();
        }}
      />
    </Box>
  );
};

const ReportSortItem = ({
  sort,
  sortField,
  fieldIndex,
  availableFields,
  handleChangeSort,
}) => {
  const fieldName = R.prop(GC.FIELD_NAME, sortField);

  const fieldLabel = R.pathOr(
    fieldName,
    [fieldName, GC.FIELD_NAME],
    R.indexBy(R.prop(GC.FIELD_VALUE), availableFields),
  );

  const direction = R.propOr(GC.SORT_TYPE_ASC, 'order', sortField);
  const directionIcon = R.equals(direction, GC.SORT_TYPE_ASC) ? I.arrowUp : I.arrowDown;

  const availableSortFields = useMemo(() => getAvailableForReportSort(
    { availableFields },
    R.remove(fieldIndex, 1, sort),
  ), [sort, fieldIndex, availableFields]);

  const handleRemove = useCallback((event) => {
    event.stopPropagation();

    handleChangeSort(R.remove(fieldIndex, 1, sort));
  }, [sort, fieldIndex, handleChangeSort]);

  const handleSave = useCallback((field) => {
    handleChangeSort(R.update(fieldIndex, field, sort));
  }, [sort, fieldIndex, handleChangeSort]);

  const iconColor = G.getTheme('colors.black');

  return (
    <ZIndexClick>
      <PopperComponent
        {...getFilterPopperSettings()}
        content={({ closePopper }) => (
          <SortEditor
            sortField={sortField}
            handleSave={handleSave}
            closePopper={closePopper}
            availableFields={availableFields}
            availableSortFields={availableSortFields}
          />
        )}
      >
        <Flex
          mx='4px'
          p='4px 6px'
          fontSize={12}
          color={iconColor}
          borderRadius='4px'
          width='fit-content'
          bg={G.getTheme('colors.bgGrey')}
        >
          <TextComponent mr='8px' title={fieldLabel} withEllipsis={true}>
            {fieldLabel}
          </TextComponent>
          <Flex mr='8px' title={direction}>
            {directionIcon(G.getTheme('colors.dark.blue'), 10, 10)}
          </Flex>
          <Box onClick={handleRemove}>
            {I.closeIcon(iconColor, 8)}
          </Box>
        </Flex>
      </PopperComponent>
    </ZIndexClick>
  );
};

ReportSortItem.defaultProps = {
  sortField: defaultSortFields,
};

export default ReportSortItem;
